import * as React from "react";

import { type SeriesColor } from "@/shared/domain/dashboards/DashboardDefinition";
import { TimeRange } from "@/shared/domain/dashboards/TimeRange";
import {
  ChartDisplayType,
  MetricDefinitionRecord,
  Period,
} from "@/shared/domain/metrics";
import {
  useAggregateMetrics,
  useInfiniteQueryMetrics,
} from "@/shared/domain/metrics/hooks";
import { NS_PER_MS } from "@/shared/time/time";
import type { Condition } from "@/types";

import { getCardTitle } from "../cardTitle";
import {
  AggregatedChartCardConfig,
  ChartCardConfig,
  effectiveChartMetric,
} from "../DashboardHeader/DashboardParams";

import { aggregateRequestFor } from "./aggregateRequest";
import type { AxisDomain } from "./axisDomain";
import { BucketWindow, instantWindow, selectionWindow } from "./bucketWindow";
import { chartSubtext, rawChartSubtext } from "./chartSubtext";
import { DrillDownDialog } from "./DrillDownDialog";
import { bucketSpan } from "./groupedSeries";
import { LoadAllButton } from "./LoadAllButton";
import { MetricChart, RawMetricChart } from "./MetricChart";
import styles from "./MetricChartCard.module.css";
import { toRawSeries } from "./rawSeries";
import { rawRequestFor } from "./rawSeriesRequest";

export interface MetricChartCardProps {
  definitions: MetricDefinitionRecord[];
  /** Configuration for this card (controlled by the parent). */
  config: ChartCardConfig;
  /** This card's position among the dashboard's unresolved cards — spreads
   *  seeded (metric-less) cards across distinct definitions (see
   *  {@link effectiveChartMetric}). */
  seedIndex?: number;
  orgId: string | undefined;
  period: Period;
  range: TimeRange;
  /** The span every card on this dashboard draws, so cards agree about what is
   *  on screen rather than each ending wherever its own metric last reported. */
  axisDomain?: AxisDomain;
  deviceIds?: string[];
  sessionIds?: string[];
  /** Optional: session-property / custom-field predicate applied server-side. */
  condition?: Condition;
  /** The dashboard's full filter vocabulary. Only the drill-down dialog reads
   *  it — it mounts the app's filter bar over the series a click covered. */
}

/** The bucket the user drilled into, pinned with the metric/period it was
 *  opened at so the dialog stays stable if the card re-resolves meanwhile. */
interface DrilldownSelection {
  metricName: string;
  bucket: BucketWindow;
  period: Period;
}

/** Everything both card variants need, with the metric already resolved. */
interface ChartCardBodyProps {
  metricName: string;
  orgId: string | undefined;
  period: Period;
  range: TimeRange;
  /** The span every card on this dashboard draws, so cards agree about what is
   *  on screen rather than each ending wherever its own metric last reported. */
  axisDomain?: AxisDomain;
  deviceIds?: string[];
  sessionIds?: string[];
  /** Optional: session-property / custom-field predicate applied server-side. */
  condition?: Condition;
  /** Optional: the card's group-by path. Both bodies split their series by it;
   *  they differ only in WHERE the split happens — the aggregated one sends it
   *  to the server, the raw one applies it to the rows that come back. */
  groupBy?: string;
  /** Optional: how the card draws its series. Absent means the line both
   *  variants have always defaulted to. */
  display?: ChartDisplayType;
}

/**
 * Full-width chart card: a compact title above the chart, plus whatever the
 * card's variant offers alongside it. Pure display — the
 * metric/series/aggregation/display selection is edited through the card's
 * ⋮ → "Edit Data" item, which opens the widget editor on its own route
 * that owns the {@link ChartCardConfig}.
 *
 * The two variants differ in more than a query, which is why each has its own
 * body: an aggregated card plots buckets and drills INTO one; a raw card
 * plots sessions, loads progressively, and navigates TO one. Splitting them
 * here is also what keeps each body's data hook unconditional.
 */
export function MetricChartCard({
  definitions,
  config,
  seedIndex = 0,
  orgId,
  period,
  range,
  axisDomain,
  deviceIds,
  sessionIds,
  condition,
}: MetricChartCardProps) {
  const names = definitions.map((d) => d.name);

  if (names.length === 0) {
    return null;
  }

  // Every field is spelled out rather than spread, so a prop added to
  // ChartCardBodyProps must be added here too. An OPTIONAL one that is not
  // simply vanishes: the type stays satisfied and the body silently falls back
  // to its undefined behaviour, which is how the shared axis domain reached
  // the card and got no further on its first pass.
  const body: ChartCardBodyProps = {
    metricName: effectiveChartMetric(config, names, seedIndex),
    orgId,
    period,
    range,
    axisDomain,
    deviceIds,
    sessionIds,
    condition,
    groupBy: config.group_by,
    display: config.display,
  };

  // Absence of an aggregation IS the raw discriminant — see
  // `RawChartCardConfig`. TypeScript narrows both ways off this test.
  return config.aggregation === undefined ? (
    <RawChartCardBody {...body} color={config.color} />
  ) : (
    <AggregatedChartCardBody {...body} config={config} />
  );
}

/**
 * An aggregated card: one point per period bucket, and a click opens the
 * drill-down dialog listing the records inside that bucket.
 *
 * The drill-down dialog is local component state (per card, so only the
 * drilled card opens); it closes on unmount, e.g. when navigating away.
 */
function AggregatedChartCardBody({
  config,
  metricName,
  orgId,
  period,
  range,
  axisDomain,
  deviceIds,
  sessionIds,
  condition,
}: ChartCardBodyProps & { config: AggregatedChartCardConfig }) {
  // The same query the chart below runs, so this reads from that cache rather
  // than fetching again (see aggregateRequestFor). Undefined while it loads —
  // the line appears with the plot rather than reserving a blank row.
  const { data } = useAggregateMetrics(
    aggregateRequestFor({
      metricName,
      period,
      aggregation: config.aggregation,
      range,
      deviceIds,
      sessionIds,
      condition,
      // Same request as the chart below, group-by included — an identical
      // request is what keeps the caption and the plot on ONE cache entry and
      // one fetch (see aggregateRequestFor).
      groupBy: config.group_by,
    }),
    orgId,
  );
  const subtext = React.useMemo(() => {
    if (!data) {
      return null;
    }
    // Every metric row belongs to exactly one group, so summing the per-bucket
    // totals still counts each session once however the response was split.
    const sessions = data.records.reduce((sum, r) => sum + r.total, 0);
    // The SPAN from first data point to last, in whole periods — the same
    // gap-filled run the chart plots on its x-axis, so the caption and the
    // plot agree about how much time is on screen. Counting records instead
    // would under-report whenever a period had no sessions at all, and
    // over-report by a factor of the group count on a grouped card.
    return chartSubtext(sessions, bucketSpan(data.records, axisDomain), period);
  }, [data, period, axisDomain]);

  const [drill, setDrill] = React.useState<DrilldownSelection | null>(null);

  const openDrill = React.useCallback(
    (bucket: BucketWindow) => setDrill({ metricName, bucket, period }),
    [metricName, period],
  );

  const closeDrill = React.useCallback(() => setDrill(null), []);

  const titleText = getCardTitle(metricName, config.aggregation);

  return (
    <div className={styles.card}>
      <div className={styles.title} title={titleText}>
        {titleText}
      </div>
      {subtext !== null && (
        <div className={styles.subtext} title={subtext}>
          {subtext}
        </div>
      )}

      <div className={styles.chartArea}>
        <MetricChart
          metricName={metricName}
          color={config.color}
          orgId={orgId}
          period={period}
          aggregation={config.aggregation}
          range={range}
          deviceIds={deviceIds}
          sessionIds={sessionIds}
          condition={condition}
          display={config.display}
          groupBy={config.group_by}
          axisDomain={axisDomain}
          onBucketClick={openDrill}
        />
      </div>

      <DrillDownDialog
        open={drill !== null}
        onClose={closeDrill}
        metricName={drill?.metricName ?? metricName}
        period={drill?.period ?? period}
        bucket={drill?.bucket ?? null}
        orgId={orgId}
        deviceIds={deviceIds}
        sessionIds={sessionIds}
        condition={condition}
      />
    </div>
  );
}

/**
 * A raw card: one point per session over the window, drawn from the first
 * page immediately, with the rest available on demand beside the caption.
 *
 * Runs the SAME `rawRequestFor(...)` query as the chart it contains, so both
 * hold one React Query cache entry and one fetch — which is what lets the
 * Load all button here extend the series drawn there.
 *
 * A click opens the SAME drill-down dialog the aggregated card uses, listing
 * the one record behind the point. It lists one row rather than jumping
 * straight to the session because the table is where a record's device,
 * invocation and value live — and because a click landing on a dialog here
 * and a dialog there is one behavior to learn instead of two.
 *
 * The dialog is given a one-nanosecond window (see `instantWindow`): a raw
 * point has no width, and the dialog's half-open `>= start AND < end` filter
 * matches nothing across a zero-width range.
 */
function RawChartCardBody({
  metricName,
  orgId,
  period,
  range,
  axisDomain,
  deviceIds,
  sessionIds,
  condition,
  groupBy,
  display,
  color,
}: ChartCardBodyProps & { color: SeriesColor | undefined }) {
  // The raw series comes from `queryMetrics`, which takes the window in
  // nanoseconds — unlike the aggregate endpoint, which takes it in ms and
  // converts as it serializes. Converted here, at the one place the two
  // conventions meet.
  //
  // The DOMAIN's bounds, not the range's, whenever there is one. `queryMetrics`
  // filters the window literally while `aggregate_metric` snaps its own with
  // date_trunc first, so a raw card reading `range` covers a narrower span than
  // the aggregated card beside it — and switching a card off aggregation
  // visibly moved its bounds. The domain is that snapped window, so asking for
  // it makes the two cards read the same rows.
  const startTimeNs =
    axisDomain?.startNs ?? BigInt(range.start_ms) * BigInt(NS_PER_MS);
  const endTimeNs =
    axisDomain?.endNs ?? BigInt(range.end_ms) * BigInt(NS_PER_MS);

  // The point that was clicked, pinned with the label its tooltip carried, so
  // the dialog titles the exact instant it is showing rather than the calendar
  // bucket the dashboard's period would name.
  const [drill, setDrill] = React.useState<{
    bucket: BucketWindow;
    label: string;
    /** The sessions the click resolved — exactly the ones the tooltip listed.
     *  Undefined on an ungrouped card, whose one point the window already
     *  pins on its own. */
    sessionIds?: string[];
  } | null>(null);

  const { data, hasNextPage, fetchNextPage } = useInfiniteQueryMetrics(
    rawRequestFor({
      metricName,
      startTimeNs,
      endTimeNs,
      deviceIds,
      sessionIds,
      condition,
      // Must match the chart's request field for field: an identical request is
      // what makes the two share one React Query entry and one fetch, so that
      // "Load all" here drains pages into the series the chart draws.
      groupBy,
    }),
    orgId,
  );

  const subtext = React.useMemo(() => {
    if (!data) {
      return null;
    }
    const { points, undated } = toRawSeries(data.pages.flatMap((p) => p.items));
    return rawChartSubtext(points.length, hasNextPage, undated);
  }, [data, hasNextPage]);

  const titleText = getCardTitle(metricName, undefined);

  return (
    <div className={styles.card}>
      <div className={styles.title} title={titleText}>
        {titleText}
      </div>
      {subtext !== null && (
        <div className={styles.subtextRow}>
          <span className={styles.subtext} title={subtext}>
            {subtext}
          </span>
          <LoadAllButton
            hasNextPage={hasNextPage}
            fetchNextPage={fetchNextPage}
          />
        </div>
      )}

      <div className={styles.chartArea}>
        <RawMetricChart
          metricName={metricName}
          color={color}
          orgId={orgId}
          startTimeNs={startTimeNs}
          endTimeNs={endTimeNs}
          deviceIds={deviceIds}
          sessionIds={sessionIds}
          condition={condition}
          display={display}
          groupBy={groupBy}
          axisDomain={axisDomain}
          onPointClick={(datum, selection) => {
            // Drill into everything the click reported, not just the point
            // aimed at: that set is what the tooltip listed, so the dialog
            // lists the same rows the reader was already looking at. A lone
            // point needs no session filter — its instant pins it.
            const resolved =
              selection.length > 1 ? selectionWindow(selection) : undefined;
            setDrill({
              bucket: resolved?.bucket ?? instantWindow(datum.start_time),
              label: datum.tooltipTitle,
              sessionIds: resolved?.sessionIds,
            });
          }}
        />
      </div>

      <DrillDownDialog
        open={drill !== null}
        onClose={() => setDrill(null)}
        metricName={metricName}
        period={period}
        bucket={drill?.bucket ?? null}
        windowLabel={drill?.label}
        orgId={orgId}
        deviceIds={deviceIds}
        // The click's own sessions when it resolved several, else the
        // dashboard's session scope. The former is already a subset of the
        // latter — the chart it was read off was drawn under it.
        sessionIds={drill?.sessionIds ?? sessionIds}
        // Carried like the aggregated card does: without it a filtered
        // dashboard shows a filtered chart and an unfiltered table.
        condition={condition}
      />
    </div>
  );
}
